<template>
  <div class="container">
    <areYouSureBox
      has-modal-card
      :active="showAreYouSure"
      @response="areYouSureResponseHandler"
    ></areYouSureBox>
    <form class="section columns">
      <div class="column is-two-fifths">
        <memberId :value="memberIdDisplayValue" />
        <b-field :label="$t('MemberPosition')" class="hide-inner-invisible">
          <b-input
            expanded
            v-model="selectedMember.positionName"
            :placeholder="canEdit ? $t('MemberPositionInput') : ''"
            data-test-id="memberPositionName"
            :disabled="canEdit === false"
            maxlength="500"
          />
        </b-field>
        <memberName
          v-model="selectedMember.memberFirstName"
          :canEdit="canEdit"
          ref="memberNameRef"
        />
        <memberEmail v-model="selectedMember.email" :canEdit="canEdit" ref="memberEmailRef"/>
        <phoneNumberInput
          v-model="selectedMember.phoneNumber"
          :canEdit="canEdit"
          :label="$t('MemberPhoneNumber')"
          :placeholder="$t('MemberPhoneNumberInput')"
        />
      </div>
      <div class="column is-two-fifths">
        <notificationGroups
        :canEdit="canEdit"
        v-model="selectedMember.groupId"
        />
        <NotificationStrategyField
        v-model="selectedMember.notificationStrategy"
        :canEdit="canEdit"
        />
         <b-field :label="$t('NotifyOn')">
         <b-checkbox
          v-model="selectedMember.allowEmail"
          :native-value="selectedMember.allowEmail"
          :true-value="true"
          :false-value="false"
          data-test-id="allowEmail"
          >
          {{$t('Email')}}
          </b-checkbox>
          <b-checkbox 
          v-model="selectedMember.allowSms"
          :native-value="selectedMember.allowSms"
          :true-value="true"
          :false-value="false"
          data-test-id="allowSms"
          >
          {{$t('Sms')}}
          </b-checkbox>
          <b-checkbox
          v-model="selectedMember.allowPhone"
          :native-value="false"
          :true-value="true"
          :false-value="false"
          data-test-id="allowPhone"
          >
          {{$t('Phone')}}
          </b-checkbox>
          <b-checkbox
          v-model="selectedMember.allowWhatsapp"
          :native-value="selectedMember.allowWhatsapp"
          :true-value="true"
          :false-value="false"
          data-test-id="allowWhatsapp"
          >
          {{$t('WhatsApp')}}
          </b-checkbox>
         </b-field>
        <b-field :label="$t('Comment')">
          <b-input
          expanded
          type="textarea"
          v-model="selectedMember.comment"
          :placeholder="canEdit ? $t('MemberCommentInput') : ''"
          data-test-id="memberComment"
          :disabled="canEdit === false"
          maxlength="500"
          />
        </b-field>
        <b-field grouped position="is-right">
          <b-field>
            <b-button @click="cancelUpdate">
             {{ canEdit ? $t("Cancel") : $t("Return") }}
            </b-button>
          </b-field>
          <b-field v-if="canEdit">
            <b-button
            @click="startProcessing"
            :disabled="isProcessing"
            data-test-id="memberDetailsSubmit"
            >
             {{ $t("Submit") }}
            </b-button>
          </b-field>
        </b-field>
      </div>
    </form>
    <section class="section" v-if="relatedShopsList.length > 0">
      <p class="title">{{ $t("Shops") }}</p>
      <shopAdministrationDetails
      :shopsList="shopsAdministrationListDetails"
      :loading="isProcessing"
      :showRelatedItems="false"
      />
    </section>
  </div>
</template>
<script setup lang="ts">
import * as Sentry from "@sentry/browser";
import { computed, ref, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAppStore, type Member, type NotificationGroup, type Shop } from '@/stores/app';
import shopsService from "@/services/shopsService";
import membersService from "@/services/membersService";
import notificationService from "@/services/notificationService";
import areYouSureBox from "@/components/AreYouSureBox.vue";
import memberId from "@/components/members/MemberId.vue";
import memberName from "@/components/members/MemberName.vue";
import memberEmail from "@/components/members/MemberEmail.vue";
import phoneNumberInput from "@/components/phoneNumberInput.vue";
import notificationGroups from "@/components/notifications/NotificationGroups.vue";
import NotificationStrategyField from "@/components/members/MemberNotificationStrategy.vue";
import shopAdministrationDetails from "@/components/shopAdministrationDetails.vue";
import routerUtils from "@/utils/routerUtils";

// Get current instance for accessing global properties
const instance = getCurrentInstance();

// Props definition - using simple approach to avoid parsing issues
const props = defineProps({
  id: {
    type: Number,
    required: false,
    default: undefined
  },
  returnTo: {
    type: String,
    required: false,
    default: '/members/admin'
  }
});

// Composables
const { t } = useI18n();
const store = useAppStore();

// Store refs
const {
  getAllowedShopsList: shopList,
  getMemberList: membersList,
  getStrategies: strategiesList,
  getChains: chainsList,
  getNotificationGroups: notificationGroupList,
} = storeToRefs(store);

const createEmptyMember = (): Member => ({
  memberId: undefined,
  canUpdateMember: false,
  memberFirstName: "",
  email: "",
  phoneNumber: 0,
  notificationStrategy: 9,
  groupId: 0,
  positionName: "",
  comment: "",
  allowPhone: false,
  allowEmail: true,
  allowSms: true,
  allowWhatsapp: false,
});

// Reactive data
const selectedMember = ref<Member>(createEmptyMember());
const originalMember = ref<Member>(createEmptyMember());
const showAreYouSure = ref(false);
const isNew = ref(false);
const processing = ref(false);

// Template refs
const memberNameRef = ref();
const memberEmailRef = ref();

// Computed properties
const isProcessing = computed(() => processing.value);

const canEdit = computed<boolean>(() => Boolean(selectedMember.value.canUpdateMember));

const isEditMode = computed<boolean>(() => typeof props.id === "number" && !Number.isNaN(props.id));

const memberIdDisplayValue = computed<number | undefined>(() => {
  if (isEditMode.value) {
    return selectedMember.value.memberId;
  }
  return undefined;
});

const relatedShopsList = computed<Shop[]>(() => {
  const groupChosen = notificationGroupList.value?.find(
    (group: NotificationGroup) => group.groupId === (selectedMember.value.groupId ?? 0)
  );
  
  if (groupChosen?.shopIds && shopList.value) {
    return shopList.value.filter((shop: Shop) => 
      groupChosen.shopIds.includes(shop.shopId)
    );
  }
  
  return [];
});

const shopsAdministrationListDetails = computed(() => {
  return shopsService.prepareShopsData(
    relatedShopsList.value as any,
    (notificationGroupList.value || []) as any,
    (membersList.value || []) as any,
    (chainsList.value || []) as any,
  );
});

// Methods
const areYouSureResponseHandler = async (action: string) => {
  if (showAreYouSure.value === true) {
    showAreYouSure.value = false;
    try {
      if (action === "yes") {
        await sendUpdate();
      }
    } finally {
      processing.value = false;
    }
  }
};

const createNewMember = () => {
  selectedMember.value = {
    ...createEmptyMember(),
    canUpdateMember: true,
  };
  isNew.value = true;
};

const startProcessing = () => {
  if (processing.value === false) {
    processing.value = true;
    const validationResults = membersService.validateMemberData(
      selectedMember.value,
      [memberNameRef.value, memberEmailRef.value]
    );
    
    if (validationResults.length > 0) {
      const translatedErrors = validationResults
        .map((validator: string) => t(validator))
        .join(", ");
      openNotification(translatedErrors, "is-danger");
      processing.value = false;
    } else {
      showAreYouSure.value = true;
    }
  }
};

const sendUpdate = async () => {
  const member = selectedMember.value;
  const distinctMember = {
    memberId: member.memberId,
    memberFirstName: member.memberFirstName,
    email: member.email,
    phoneNumber: member.phoneNumber,
    notificationGroupId: member.groupId,
    notificationStrategyId: member.notificationStrategy,
    positionName: member.positionName || undefined,
    comment: member.comment || undefined,
    allowPhone: member.allowPhone,
    allowEmail: member.allowEmail,
    allowSms: member.allowSms,
    allowWhatsapp: member.allowWhatsapp,
  };

  try {
    await membersService.postMember(distinctMember);
    openNotification(t("SuccessMemberUpdated"), "is-success");
    resetMember();
    await membersService.getMembersList();
    routerUtils.moveTo(props.returnTo);
  } catch (error: any) {
    if (error?.response && error.response.status === 409) {
      Object.getOwnPropertyNames(error.response.data?.errors).forEach((err: string) => {
        openNotification(t(err), "is-danger");
      });
    } else {
      openNotification(t("ErrorContactUs"), "is-danger");
      Sentry.captureException(error);
    }
  }
};

const cacheOriginalMember = (member: Member) => {
  if (member) {
    selectedMember.value = { ...member };
    originalMember.value = { ...member };
  }
};

const openNotification = (message: string, type: string) => {
  // Access $buefy through the current instance
  const buefy = instance?.appContext.config.globalProperties.$buefy;
  if (buefy) {
    const notificationConfig = {
      message,
      type,
      hasIcon: true,
      indefinite: true,
      closeable: true,
      position: "is-top" as const,
      ...(type === "is-success" ? { autoClose: true, duration: 5000 } : {}),
      "data-test-id": "memberValidationMessage",
    };

    buefy.notification.open(notificationConfig);
  }
};

const revertToOriginalMember = () => {
  Object.assign(selectedMember.value, originalMember.value);
};

const resetMember = () => {
  selectedMember.value = createEmptyMember();
  isNew.value = false;
};

const cancelUpdate = () => {
  routerUtils.moveTo(props.returnTo);
};

const getMemberToEdit = () => {
  const member = membersList.value?.find((x) => x.memberId === props.id);
  if (member) {
    cacheOriginalMember(member);
  }
};

// Lifecycle hooks
onMounted(async () => {
  if (shopList.value === null) {
    await shopsService.getAllowedShopsList();
  }
  if (notificationGroupList.value === null) {
    await notificationService.getNotificationGroups();
  }
  if (membersList.value === null) {
    await membersService.getMembersList();
  }
  if (!strategiesList.value) {
    await notificationService.getStrategies();
  }
  if (!chainsList.value) {
    await shopsService.getChains();
  }

  if (isEditMode.value) {
    getMemberToEdit();
  } else {
    createNewMember();
  }
});

onBeforeUnmount(() => {
  revertToOriginalMember();
  resetMember();
});
</script>
<style>
.hide-inner-invisible .is-invisible{
    display: none;
}
</style>
